import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";

import { QuoteValue } from "../types";

type DistrictComparisonExportParams = {
    districtComparisionData: any;
    commodity: string | null;
    monthYear: string;
    contractType: string[] | null;
    benchmark: string;
    quotes: QuoteValue;
};

export const getDistrictComparisonBaseSkuLabel = (
    commodity: string | null,
    benchmark: string,
) => {
    if (commodity === "Steel Conduit") {
        return "3/4 EMT";
    }

    if (commodity === "PVC Conduit") {
        return "4 PVC 40";
    }

    return benchmark || "";
};

export const downloadDistrictComparisonExcel = async ({
    districtComparisionData,
    commodity,
    monthYear,
    contractType,
    benchmark,
    quotes,
}: DistrictComparisonExportParams) => {
    const data = districtComparisionData?.chartData ?? [];
    const monthlyTL = districtComparisionData?.tableData?.monthlyTL ?? [];

    if (data.length === 0) {
        return;
    }

    const vendorSet = new Set<string>();
    data.forEach((row: any) => {
        Object.keys(row).forEach((key) => {
            if (key !== "district") {
                vendorSet.add(key);
            }
        });
    });
    const vendors = Array.from(vendorSet).sort();

    const districtSet = new Set<string>();
    monthlyTL.forEach((row: any) => districtSet.add(row.district));
    data.forEach((row: any) => districtSet.add(row.district));
    const districts = Array.from(districtSet).sort();

    const chartMap = new Map<string, Record<string, number>>();
    data.forEach((row: any) => {
        const { district, ...rest } = row;
        chartMap.set(district, rest as Record<string, number>);
    });

    const periodLabel = commodity === "Copper Wire" ? "Week" : "Month";
    const periodValue = monthYear
        ? commodity === "Copper Wire"
            ? monthYear
            : moment(monthYear).format("MMM-YY")
        : "";

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("District Comparison");
    const baseSkuLabel = getDistrictComparisonBaseSkuLabel(commodity, benchmark);

    worksheet.addRow(["Commodity:", commodity || ""]);
    worksheet.addRow([`${periodLabel}:`, periodValue]);
    worksheet.addRow([
        "Quote:",
        commodity === "Copper Wire"
            ? contractType?.[0]
            : quotes === "11"
                ? "Latest Quote"
                : quotes ?? "",
    ]);
    worksheet.addRow([]);

    const title = worksheet.addRow([`${baseSkuLabel} Supplier Quote`]);
    worksheet.mergeCells(title.number, 1, title.number, vendors.length + 1);
    title.font = { bold: true };
    title.alignment = { horizontal: "left" };

    const header = worksheet.addRow(["District", ...vendors.map((vendor) => vendor.toUpperCase())]);
    header.font = { bold: true };
    header.alignment = { horizontal: "center", vertical: "middle" };

    districts.forEach((district) => {
        const rowValues = chartMap.get(district) || {};
        const values = vendors.map((vendor) => {
            const value = rowValues[vendor];
            return typeof value === "number" ? value : "-";
        });
        worksheet.addRow([district, ...values]);
    });

    const lastCol = vendors.length + 1;

    worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };
            cell.alignment = {
                vertical: "middle",
                horizontal:
                    rowNumber <= header.number
                        ? "center"
                        : colNumber === 1
                            ? "left"
                            : "right",
                wrapText: false,
            };
        });

        if (rowNumber === header.number) {
            row.height = 22;
        }
    });

    worksheet.getColumn(1).width = 18;
    for (let colIndex = 2; colIndex <= lastCol; colIndex += 1) {
        const column = worksheet.getColumn(colIndex);
        column.width = 12;
        column.numFmt = "$#,##0.00";
    }

    const fileName = `District_Comparison_${(commodity || "").replace(/\s+/g, "_")}_${periodValue}.xlsx`;
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), fileName);
};
